<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  createdAt: {
    type: [String, Number],
    default: '',
  },
  deliveryReport: {
    type: Object,
    default: null,
  },
  canPermanentDelete: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'hardDelete',
  'reprocess',
  'pause',
  'resume',
  'refresh',
]);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PAUSED = 'paused';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();

const isCompleted = computed(
  () => !props.isLiveChatType && props.status === STATUS_COMPLETED
);

const isInactive = computed(() => !props.isEnabled);

const isScheduled = computed(
  () =>
    !props.isLiveChatType &&
    ![STATUS_COMPLETED, STATUS_PAUSED, STATUS_PROCESSING].includes(props.status)
);

const statusBadgeClass = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? 'bg-n-teal-3 text-n-teal-11 outline outline-1 outline-n-teal-7'
      : 'bg-n-slate-4 text-n-slate-11 outline outline-1 outline-n-slate-7';
  }

  if (isInactive.value) {
    return 'bg-n-slate-4 text-n-slate-11 outline outline-1 outline-n-slate-7';
  }

  if (props.status === STATUS_COMPLETED) {
    return 'bg-n-teal-3 text-n-teal-11 outline outline-1 outline-n-teal-7';
  }

  if (props.status === STATUS_PAUSED) {
    return 'bg-n-amber-3 text-n-amber-11 outline outline-1 outline-n-amber-7';
  }

  if (props.status === STATUS_PROCESSING) {
    return 'bg-n-amber-3 text-n-amber-11 outline outline-1 outline-n-amber-7';
  }

  return 'bg-n-blue-3 text-n-blue-11 outline outline-1 outline-n-blue-7';
});

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (isInactive.value) {
    return 'Inativa';
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PAUSED) {
    return 'Pausada';
  }

  if (props.status === STATUS_PROCESSING) {
    return 'Em envio';
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});

const recipients = computed(() => props.deliveryReport?.recipients || []);
const canPause = computed(
  () => !props.isLiveChatType && props.status === 'active'
);
const canResume = computed(
  () => !props.isLiveChatType && props.status === STATUS_PAUSED
);
const showRecipientsDialog = ref(false);
const recipientsRefreshTimer = ref(null);
const recipientsDialogTitle = 'Números da campanha';
const emptyRecipientsMessage =
  'Nenhum número registrado para esta campanha ainda.';
const createdAtPrefix = 'Criada em';
const scheduledAtPrefix = 'Agendada para';
const deliveredAtPrefix = 'Entregue em';
const actionLabels = {
  recipients: 'Ver números e entregas',
  edit: 'Editar campanha',
  reprocess: 'Reprocessar campanha',
  pause: 'Pausar campanha',
  resume: 'Retomar campanha',
  settings: 'Configurar campanha',
  delete: 'Excluir campanha',
  hardDelete: 'Excluir definitivamente',
};

const recipientGroups = computed(() => ({
  entregue: recipients.value.filter(
    recipient => recipient.status === 'entregue'
  ),
  aguardando: recipients.value.filter(
    recipient => recipient.status === 'aguardando'
  ),
  erro: recipients.value.filter(
    recipient => recipient.status === 'nao_entregue'
  ),
}));

const recipientSummary = computed(() => [
  {
    key: 'entregue',
    label: 'Entregues',
    count: recipientGroups.value.entregue.length,
    icon: 'i-lucide-check-check',
    class: 'text-n-teal-11 bg-n-teal-3',
  },
  {
    key: 'aguardando',
    label: 'Aguardando',
    count: recipientGroups.value.aguardando.length,
    icon: 'i-lucide-clock-3',
    class: 'text-n-amber-11 bg-n-amber-3',
  },
  {
    key: 'erro',
    label: 'Erro no envio',
    count: recipientGroups.value.erro.length,
    icon: 'i-lucide-circle-alert',
    class: 'text-n-ruby-11 bg-n-ruby-3',
  },
]);

const statusLabel = status => {
  if (status === 'entregue') return 'Entregue';
  if (status === 'nao_entregue') return 'Erro no envio';
  return 'Aguardando';
};

const statusClass = status => ({
  'text-n-teal-11': status === 'entregue',
  'text-n-ruby-11': status === 'nao_entregue',
  'text-n-amber-11': status === 'aguardando',
});

const formatDateTime = value => {
  if (!value) return '';

  const timestamp = typeof value === 'number' ? value * 1000 : value;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('pt-BR');
};

const createdAtLabel = computed(() => formatDateTime(props.createdAt));

const formatRecipientDeliveredAt = recipient => {
  if (recipient.status !== 'entregue' || !recipient.delivered_at) return '';

  return new Date(recipient.delivered_at).toLocaleString('pt-BR');
};

const stopRecipientsRefresh = () => {
  if (!recipientsRefreshTimer.value) return;

  clearInterval(recipientsRefreshTimer.value);
  recipientsRefreshTimer.value = null;
};

watch(showRecipientsDialog, isOpen => {
  stopRecipientsRefresh();
  if (!isOpen) return;

  emit('refresh');
  recipientsRefreshTimer.value = setInterval(() => emit('refresh'), 5000);
});

onBeforeUnmount(stopRecipientsRefresh);
</script>

<template>
  <CardLayout
    layout="col"
    :class="{
      'campaign-card-completed': isCompleted,
    }"
  >
    <div class="flex w-full items-start justify-between gap-4">
      <div
        class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2"
      >
        <div class="flex justify-between gap-3 w-fit">
          <span
            class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
          >
            {{ title }}
          </span>
          <span
            class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
            :class="statusBadgeClass"
          >
            {{ campaignStatus }}
          </span>
        </div>
        <div
          v-dompurify-html="formatMessage(message, false, false, false)"
          class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
        />
        <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
          <LiveChatCampaignDetails
            v-if="isLiveChatType"
            :sender="sender"
            :inbox-name="inboxName"
            :inbox-icon="inboxIcon"
          />
          <SMSCampaignDetails
            v-else
            :inbox-name="inboxName"
            :inbox-icon="inboxIcon"
            :scheduled-at="scheduledAt"
            :delivery-report="deliveryReport"
          />
        </div>
        <div
          v-if="createdAtLabel"
          class="flex items-center w-full gap-1.5 overflow-hidden text-sm text-n-slate-11"
        >
          <span class="i-lucide-calendar-plus size-3.5 flex-shrink-0" />
          <span class="flex-shrink-0">{{ createdAtPrefix }}</span>
          <span class="truncate font-medium text-n-slate-12">
            {{ createdAtLabel }}
          </span>
        </div>
        <div
          v-if="!isLiveChatType && scheduledAt"
          class="flex items-center w-full gap-1.5 overflow-hidden text-sm text-n-slate-11"
        >
          <span class="i-lucide-calendar-clock size-3.5 flex-shrink-0" />
          <span class="flex-shrink-0">{{ scheduledAtPrefix }}</span>
          <span class="truncate font-medium text-n-slate-12">
            {{ formatDateTime(scheduledAt) }}
          </span>
        </div>
      </div>
      <div class="flex items-center justify-end gap-2">
        <Button
          v-if="!isLiveChatType"
          v-tooltip.bottom="actionLabels.recipients"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-list-checks"
          :title="actionLabels.recipients"
          :aria-label="actionLabels.recipients"
          @click.stop="showRecipientsDialog = true"
        />
        <Button
          v-if="!isLiveChatType && !isScheduled"
          v-tooltip.bottom="actionLabels.edit"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-pencil"
          :title="actionLabels.edit"
          :aria-label="actionLabels.edit"
          @click.stop="emit('edit')"
        />
        <Button
          v-if="!isLiveChatType && !isScheduled"
          v-tooltip.bottom="actionLabels.reprocess"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-rotate-cw"
          :title="actionLabels.reprocess"
          :aria-label="actionLabels.reprocess"
          @click.stop="emit('reprocess')"
        />
        <Button
          v-if="canPause"
          v-tooltip.bottom="actionLabels.pause"
          variant="faded"
          color="amber"
          size="sm"
          icon="i-lucide-pause"
          :title="actionLabels.pause"
          :aria-label="actionLabels.pause"
          @click.stop="emit('pause')"
        />
        <Button
          v-if="canResume"
          v-tooltip.bottom="actionLabels.resume"
          variant="faded"
          color="teal"
          size="sm"
          icon="i-lucide-play"
          :title="actionLabels.resume"
          :aria-label="actionLabels.resume"
          @click.stop="emit('resume')"
        />
        <Button
          v-if="isLiveChatType"
          v-tooltip.bottom="actionLabels.settings"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-sliders-vertical"
          :title="actionLabels.settings"
          :aria-label="actionLabels.settings"
          @click.stop="emit('edit')"
        />
        <Button
          v-tooltip.bottom="actionLabels.delete"
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          :title="actionLabels.delete"
          :aria-label="actionLabels.delete"
          @click.stop="emit('delete')"
        />
        <Button
          v-if="canPermanentDelete"
          v-tooltip.bottom="actionLabels.hardDelete"
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          :title="actionLabels.hardDelete"
          :aria-label="actionLabels.hardDelete"
          @click.stop="emit('hardDelete')"
        />
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showRecipientsDialog"
        class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black8 p-4 backdrop-blur-sm"
        @click.self="showRecipientsDialog = false"
      >
        <div
          class="flex w-full max-w-3xl flex-col gap-5 rounded-xl border border-n-weak bg-n-solid-2 p-6 shadow-xl"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h3 class="mb-1 text-lg font-medium text-n-slate-12">
                {{ recipientsDialogTitle }}
              </h3>
              <p class="mb-0 truncate text-sm text-n-slate-11">
                {{ title }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-x"
              @click="showRecipientsDialog = false"
            />
          </div>

          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div
              v-for="item in recipientSummary"
              :key="item.key"
              class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
            >
              <span
                class="flex size-9 items-center justify-center rounded-lg"
                :class="item.class"
              >
                <span :class="item.icon" class="size-4" />
              </span>
              <div>
                <p class="mb-0 text-xs text-n-slate-11">{{ item.label }}</p>
                <p class="mb-0 text-lg font-semibold text-n-slate-12">
                  {{ item.count }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="recipients.length"
            class="max-h-[55vh] overflow-y-auto rounded-lg border border-n-weak"
          >
            <div
              v-for="recipient in recipients"
              :key="`${recipient.phone_number}-${recipient.updated_at}`"
              class="grid grid-cols-[1fr_auto] gap-3 border-b border-n-weak px-4 py-3 text-sm last:border-b-0"
            >
              <div class="min-w-0">
                <p class="mb-0 truncate text-n-slate-12">
                  {{ recipient.phone_number || '-' }}
                </p>
                <p class="mb-0 truncate text-xs text-n-slate-11">
                  {{ recipient.contact_name || 'Contato sem nome' }}
                </p>
                <p
                  v-if="formatRecipientDeliveredAt(recipient)"
                  class="mb-0 truncate text-xs text-n-slate-10"
                >
                  {{ deliveredAtPrefix }}
                  {{ formatRecipientDeliveredAt(recipient) }}
                </p>
              </div>
              <span
                class="self-center whitespace-nowrap rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium"
                :class="statusClass(recipient.status)"
              >
                {{ statusLabel(recipient.status) }}
              </span>
            </div>
          </div>
          <div
            v-else
            class="rounded-lg border border-dashed border-n-weak bg-n-alpha-1 px-4 py-8 text-center text-sm text-n-slate-11"
          >
            {{ emptyRecipientsMessage }}
          </div>
        </div>
      </div>
    </Teleport>
  </CardLayout>
</template>

<style scoped>
.campaign-card-completed {
  position: relative;
  outline-color: rgb(var(--teal-8));
  box-shadow:
    0 0 0 1px rgba(var(--teal-9), 0.75),
    0 0 18px rgba(var(--teal-9), 0.32),
    inset 0 0 18px rgba(var(--teal-9), 0.12);
  overflow: hidden;
}

.campaign-card-completed::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    115deg,
    transparent 0%,
    transparent 30%,
    rgba(var(--teal-9), 0.06) 40%,
    rgba(var(--teal-9), 0.42) 50%,
    rgba(var(--teal-9), 0.08) 60%,
    transparent 70%,
    transparent 100%
  );
  transform: translateX(-140%);
  animation: campaign-card-laser 2.4s ease-in-out infinite;
}

.campaign-card-completed::after {
  content: '';
  position: absolute;
  inset: -1px;
  pointer-events: none;
  border-radius: inherit;
  border: 1px solid rgba(var(--teal-9), 0.8);
  filter: drop-shadow(0 0 8px rgba(var(--teal-9), 0.55));
}

@keyframes campaign-card-laser {
  0%,
  38% {
    transform: translateX(-140%);
  }

  72%,
  100% {
    transform: translateX(140%);
  }
}
</style>
